import React from 'react';
import { motion } from 'framer-motion';
import { Mail, Zap, Settings, Check } from 'lucide-react';

// Define the type for each step
interface Step {
  number: number;
  title: string;
  description: string;
  imageUrl: string;
  icon: React.ReactNode;
}

function HowItWorksSection() {
  const steps: Step[] = [
    {
      number: 1,
      title: "Connect Your Inbox",
      description: "Securely connect your email account with just a few clicks. We support Gmail, Outlook, Yahoo Mail, and more.",
      imageUrl: "/api/placeholder/500/300",
      icon: <Mail className="w-5 h-5" />
    },
    {
      number: 2,
      title: "AI Analyzes Your Emails",
      description: "Our smart AI scans your inbox, learns your patterns, and identifies cleaning opportunities without reading sensitive content.",
      imageUrl: "/api/placeholder/500/300",
      icon: <Zap className="w-5 h-5" />
    },
    {
      number: 3,
      title: "Review AI Recommendations",
      description: "Get intelligent suggestions for organizing, archiving, and responding to emails based on your preferences.",
      imageUrl: "/api/placeholder/500/300",
      icon: <Settings className="w-5 h-5" />
    },
    {
      number: 4,
      title: "Enjoy a Clutter-Free Inbox",
      description: "Set up automated routines that keep your inbox clean and organized, saving you hours every week.",
      imageUrl: "/api/placeholder/500/300",
      icon: <Check className="w-5 h-5" />
    }
  ];

  // Framer Motion variants for animations
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { 
        duration: 0.8, 
        staggerChildren: 0.3 
      },
    },
  };

  const itemVariants = {
    hidden: { 
      opacity: 0, 
      y: 50
    },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { 
        type: "spring",
        stiffness: 100,
        damping: 10,
        duration: 0.6 
      } 
    },
    hover: {
      scale: 1.02,
      transition: { duration: 0.2 }
    }
  };

  return (
    <motion.section 
      id="how-it-works"
      className="py-20 px-4 bg-white"
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.2 }}
      variants={containerVariants}
    >
      <div className="container mx-auto max-w-6xl">
        <motion.div 
          className="text-center mb-16"
          variants={itemVariants}
        >
          <h2 className="text-3xl md:text-4xl font-bold mb-4 text-[#694e42]">
            How MailMaid Works
          </h2>
          <p className="text-lg text-[#8a7266] max-w-2xl mx-auto">
            Get your inbox under control in minutes, not hours. Our AI does the heavy lifting for you.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-10 relative">
          {/* Connecting line (visible on desktop) */}
          <div className="absolute top-16 left-[10%] right-[10%] hidden md:block">
            <div className="h-2 border-t-2 border-dashed border-[#ff8c42]/30"></div>
          </div>

          {steps.map((step) => (
            <motion.div 
              key={step.number}
              className="relative"
              variants={itemVariants}
              whileHover="hover"
            >
              {/* Step Number Circle */}
              <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-10">
                <div className="flex items-center justify-center w-12 h-12 rounded-full bg-[#ff8c42] text-white text-xl font-bold shadow-lg">
                  {step.number}
                </div>
              </div>

              {/* Step Content */}
              <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-md h-full flex flex-col pt-10">
                <div className="flex items-center gap-2 mb-4">
                  <div className="bg-[#fff5eb] p-2 rounded-lg">
                    {step.icon}
                  </div>
                  <h3 className="text-xl font-bold text-[#694e42]">
                    {step.title}
                  </h3>
                </div>
                
                <p className="text-[#8a7266] mb-6 flex-grow">
                  {step.description}
                </p>

                {/* Step Image */}
                <div className="bg-[#f9f6f4] rounded-lg overflow-hidden h-48 mt-auto relative">
                  <img
                    src={step.imageUrl}
                    alt={`Step ${step.number}: ${step.title}`}
                    className="w-full h-full object-cover"
                  />
                  
                  {/* Overlay with icon */}
                  <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-[#ff8c42]/10 to-[#946e5d]/10">
                    <div className="bg-white/80 backdrop-blur-sm p-3 rounded-full">
                      {step.icon}
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Call to action */}
        <motion.div 
          className="text-center mt-16"
          variants={itemVariants}
        >
          <motion.button
            className="px-8 py-3 bg-[#ff8c42] text-white rounded-lg font-medium shadow-md"
            whileHover={{ scale: 1.05, boxShadow: "0 10px 15px rgba(255, 140, 66, 0.2)" }}
            whileTap={{ scale: 0.95 }}
          >
            Start Your Free Trial
          </motion.button>
          <p className="text-[#8a7266] mt-4">
            No credit card required. Free plan available.
          </p>
        </motion.div>
      </div>
    </motion.section>
  );
}

export default HowItWorksSection;